import TopPanelSearchUtils, {FieldToFocusOptions, FieldToFocusSearchItem} from "@/utils/common/TopPanelSearchUtils.ts";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {GridApi} from "ag-grid-community";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import Translator from "@/utils/common/Translator.ts";
import SecurityUtils from "@/utils/login/SecurityUtils.ts";
import MetaData from "@/utils/common/MetaData.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import BlockUtils, {type Block} from "@/utils/common/BlockUtils.ts";
import FieldTypesUtils, {type Field} from "@/utils/common/FieldTypesUtils.ts";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import IdsAndConstants from "@/utils/UIConstants/IdsAndConstants.ts";
import EditScreenPage = namaTypes.EditScreenPage;

export default class FieldToFocusUtils {
  private blockElement: HTMLDivElement | null = null;
  private targetElement: HTMLElement | null | undefined = null;
  private elementRoot: HTMLElement | null | undefined = null;
  static timeOutId: NodeJS.Timeout | null;
  private fieldId: string = '';
  private readonly presenter: EditPresenter;

  constructor(presenter: EditPresenter) {
    this.presenter = presenter
  }

  public async focusToFieldHandler(searchItem: FieldToFocusSearchItem, fieldToFocusOptions: FieldToFocusOptions): Promise<FieldToFocusUtils | undefined> {
    if (!searchItem.id)
      return;
    this.fieldId = searchItem.id
    const {pageTitle, block, blockId} = searchItem.record?.extraData;
    const page = document.querySelector(`[data-page-title=${pageTitle}]`);
    if (!page) return;
    this.blockElement = (page.querySelector(`div.${block['namaClassTypeId']}.q-expansion-item[id='${blockId}']`) ?? page) as HTMLDivElement;
    if (BlockUtils.blockIsEditScreenGroup(block)) {
      this.focusToInput();
    } else if (BlockUtils.blockIsEditScreenGrid(block)) {
      this.targetElement = this.blockElement?.querySelector(`[role=columnheader][col-id='${AGGridUtils.encodeColId(this.fieldId)}']`) as HTMLDivElement
    } else if (BlockUtils.blockIsEditScreenActionsBlock(block)) {
      this.focusToActionButton(page);
    }
    if (!BlockUtils.blockIsEditScreenGrid(block))
      this.scrollToFocusedField(page);
    if (!fieldToFocusOptions?.dontFlash)
      this.highlightFieldToFocus();
    return this
  }

  static async focusToAgColumn(presenter: EditPresenter, gridApi: GridApi<any> | undefined, fieldToFocusOptions: FieldToFocusOptions, searchItem: FieldToFocusSearchItem) {
    if (!gridApi)
      return;
    const instance = await new FieldToFocusUtils(presenter).focusToFieldHandler(searchItem, fieldToFocusOptions);
    if (!instance)
      return;
    const colId = instance.fieldId;
    if (ObjectChecker.isEmptyOrNull(colId))
      return;
    if (searchItem.record.extraData.agRowIndex === IdsAndConstants.MRN_REFRESH_CELLS) {
      gridApi.refreshCells();
      return;
    }
    let rowIndex = searchItem.record.extraData.agRowIndex ?? 0;
    const realRowIndex = AGGridUtils.getDisplayedRowIndex(gridApi, rowIndex);
    if (ObjectChecker.isNullOrUndefined(realRowIndex))
      return;
    gridApi.ensureIndexVisible(realRowIndex);
    gridApi.setFocusedCell(realRowIndex, AGGridUtils.encodeColId(colId));
  }

  private focusToInput() {
    this.targetElement = this.blockElement?.querySelector(`[data-field-id='${this.fieldId}']`)
    if (this.targetElement)
      this.elementRoot = this.targetElement.closest("div[data-input-field]") as HTMLDivElement | null;
    const inputElementToFocus = this.targetElement?.querySelector("input");
    if (inputElementToFocus)
      (inputElementToFocus as HTMLInputElement).focus();
  }

  private focusToActionButton(page: Element) {
    this.targetElement = page.querySelector(`button[data-action-id=${this.fieldId}]`) as HTMLButtonElement | null;
    this.elementRoot = this.targetElement?.closest("div[data-actions-block]") as HTMLDivElement | null;
  }

  private scrollToFocusedField(page: Element) {
    const scrollbar = page.querySelector(`div.q-scrollarea__container`) as HTMLDivElement | null;
    if (scrollbar)
      scrollbar.scrollTo({
        top: (this.blockElement?.offsetTop ?? 0) + (this.elementRoot?.offsetTop ?? 0),
        behavior: "smooth",
      });
  }

  private highlightFieldToFocus() {
    if (FieldToFocusUtils.timeOutId)
      clearTimeout(FieldToFocusUtils.timeOutId)
    document.querySelectorAll('.nama-field-to-focus').forEach(el => {
      el.classList.remove('nama-field-to-focus');
    });
    this.targetElement?.classList.add("nama-field-to-focus");
    FieldToFocusUtils.timeOutId = setTimeout(() => {
      document.querySelectorAll('.nama-field-to-focus').forEach(el => {
        el.classList.remove('nama-field-to-focus');
        useNamaFlagsStore().setFieldToFocusOptions({});
      });
    }, 12000);
  }

  private createSearchItem(presenterId: number | undefined, page: EditScreenPage, pageIndex: number, block: Block, field: Field, blockId: string, row?: number): FieldToFocusSearchItem | undefined {
    const fieldId: string | undefined = field?.id;
    if (!fieldId)
      return;
    if (BlockUtils.blockIsEditScreenGroup(block) && !this.presenter.isValidFieldForAttach(field))
      return;
    let entityType = this.presenter.entityType;
    let text = Translator.translateTitledId(entityType, field)! + `(${fieldId})`;
    if (BlockUtils.blockIsEditScreenGrid(block))
      text = Translator.translateId(fieldId.split(".")[1]) + `(${fieldId})`
    let ar = Translator.isArabic() ? Translator.translateTitledId(entityType, field) : Translator.translateTitledIdInOtherLang(entityType, field);
    let en = Translator.isEnglish() ? Translator.translateTitledId(entityType, field) : Translator.translateTitledIdInOtherLang(entityType, field);
    return {
      presenterId,
      id: fieldId,
      ar: ar,
      en: en,
      type: "text",
      text,
      searchData: fieldId.toLowerCase() + " " + ar.toLowerCase() + " " + StringUtils.normalizeArabic(ar.toLowerCase()) + " " +
        Translator.translateIdAr(fieldId).toLowerCase() + " " + StringUtils.normalizeArabic(Translator.translateIdAr(fieldId).toLowerCase()) + " " +
        en.toLowerCase() + " " + Translator.translateIdEn(fieldId).toLowerCase(),
      record: {
        extraData: {
          block,
          field,
          presenter: this.presenter,
          pageTitle: page.title!.resourceId!,
          blockId,
          agRowIndex: row, pageIndex
        }
      }
    };
  }

  fieldSearcher(searchValue: string, presenter?: EditPresenter, row?: number): FieldToFocusSearchItem[] {
    presenter = presenter || this.presenter
    searchValue = searchValue.toLowerCase()
    const searchResult: FieldToFocusSearchItem[] = []
    for (let [pageIndex, page] of presenter.pages.value.entries()) {
      for (let [blockIndex, block] of page.blocks!.entries()) {
        if (!BlockUtils.isValidBlockForAttach(this.presenter, block))
          continue;
        const blockId = block.title && block.title.resourceId ? block.title.resourceId : 'p' + pageIndex + 'g' + blockIndex
        if (BlockUtils.blockIsEditScreenGroup(block) && block.fields) {
          for (const field of block.fields) {
            const item = this.createSearchItem(presenter?.presenterId, page, pageIndex, block, field, blockId, row);
            if (TopPanelSearchUtils.isValueInSearchItem(item, searchValue))
              searchResult.push(item);
            if (FieldTypesUtils.fieldIsCompositeEditScreenField(field) && field.subFields) {
              for (const subField of field.subFields) {
                const subItem = this.createSearchItem(presenter?.presenterId, page, pageIndex, block, subField, blockId, row);
                if (TopPanelSearchUtils.isValueInSearchItem(subItem, searchValue))
                  searchResult.push(subItem)
              }
            }
          }
        } else if (BlockUtils.blockIsEditScreenGrid(block) && block.columns) {
          for (const column of block.columns) {
            if (!column.showInGridView || (column.showOnlyInLang && column.showOnlyInLang !== Translator.currentLang()))
              continue;
            if (!SecurityUtils.userCanView(this.presenter.entityType, column.id!, this.presenter.calcEditModeApplicability()))
              continue;
            let fieldMetadata = MetaData.findFieldMetadata(column.id!, this.presenter.fetchMetadata());
            if (fieldMetadata && !SecurityUtils.userCanFeature(fieldMetadata.relatedToFeature))
              continue;
            const item: FieldToFocusSearchItem | undefined = this.createSearchItem(presenter?.presenterId, page, pageIndex, block, column, blockId, row)
            if (TopPanelSearchUtils.isValueInSearchItem(item, searchValue))
              searchResult.push(item)
          }
        } else if (BlockUtils.blockIsEditScreenActionsBlock(block) && block.actions) {
          for (const field of block.actions) {
            const item: FieldToFocusSearchItem | undefined = this.createSearchItem(presenter?.presenterId, page, pageIndex, block, field, blockId, row)
            if (TopPanelSearchUtils.isValueInSearchItem(item, searchValue))
              searchResult.push(item)
          }
        }
      }
    }
    return searchResult
  }

  async focusFieldFromQueryParam(): Promise<boolean> {
    const searchValue = this.presenter.place.focusOnField
    if (!searchValue)
      return false;
    const fieldsToFocus = this.fieldSearcher(searchValue)
    if (!fieldsToFocus || !fieldsToFocus.length)
      return false;
    useNamaFlagsStore().setFieldToFocusOptions({field: fieldsToFocus[0]})
    return true
  }

  static async focusToField(fieldsToFocusUtils: FieldToFocusUtils, fieldId: string, row?: number) {
    const fieldsToFocus = fieldsToFocusUtils.fieldSearcher(fieldId, undefined, row)
    if (fieldsToFocus && fieldsToFocus.length)
      useNamaFlagsStore().setFieldToFocusOptions({field: fieldsToFocus[0]})
  }
}
